/**
 * Contrôles de réglage réutilisables.
 *
 * Interrupteurs, curseurs et lignes cliquables du style Material 3, partagés
 * entre l'écran des réglages généraux et celui d'une trace. Les contrôles
 * lisent et écrivent par accesseurs plutôt que d'être liés à une source
 * précise : le même interrupteur sert une préférence globale ou un champ de
 * trace.
 */

export interface SwitchOptions {
  readonly label: string
  readonly get: () => boolean
  /**
   * Applique la valeur choisie.
   *
   * Peut rendre une promesse quand l'application demande un travail —
   * relire un fichier, par exemple. L'interrupteur est alors bloqué le temps
   * de l'attente, et **revient à sa position d'origine si la promesse rend
   * `false`** : un interrupteur qui reste sur une position que l'application
   * n'a pas pu tenir ment sur l'état de la trace.
   */
  readonly set: (value: boolean) => void | Promise<boolean>
  /** Rendu grisé et non modifiable, avec la raison en sous-titre. */
  readonly disabled?: boolean
  readonly summary?: string
  readonly onChange?: () => void
}

/**
 * Ligne comportant un libellé à gauche et un interrupteur à droite.
 *
 * Sans sous-titre, la ligne est renvoyée telle quelle : l'envelopper dans un
 * conteneur inutile changerait la façon dont l'espacement du parent s'applique.
 */
export function switchRow(options: SwitchOptions): HTMLElement {
  const row = document.createElement('div')
  row.className = 'setting-row'

  const label = document.createElement('span')
  label.className = 'setting-row__label'
  label.textContent = options.label

  const control = document.createElement('label')
  control.className = 'switch'

  const input = document.createElement('input')
  input.type = 'checkbox'
  input.checked = options.get()
  input.disabled = options.disabled === true
  input.setAttribute('aria-label', options.label)
  input.addEventListener('change', () => {
    const applied = options.set(input.checked)
    if (applied === undefined) {
      options.onChange?.()
      return
    }

    const wanted = input.checked
    input.disabled = true
    void applied.then((accepted) => {
      input.disabled = options.disabled === true
      input.checked = accepted ? wanted : !wanted
      options.onChange?.()
    })
  })

  const track = document.createElement('span')
  track.className = 'switch__track'
  const thumb = document.createElement('span')
  thumb.className = 'switch__thumb'

  control.append(input, track, thumb)
  row.append(label, control)

  if (options.disabled === true) {
    row.style.opacity = '0.5'
  }

  return options.summary === undefined ? row : withSummary(row, options.summary)
}

export interface SliderOptions {
  readonly label: string
  readonly min: number
  readonly max: number
  readonly step: number
  readonly get: () => number
  readonly set: (value: number) => void
  readonly format: (value: number) => string
  readonly onChange?: () => void
}

/** Titre, valeur courante à droite, curseur en dessous. */
export function sliderRow(options: SliderOptions): HTMLElement {
  const wrapper = document.createElement('div')
  wrapper.className = 'setting-slider'

  const header = document.createElement('div')
  header.className = 'setting-slider__header'

  const label = document.createElement('span')
  label.textContent = options.label

  const value = document.createElement('span')
  value.className = 'setting-slider__value'

  header.append(label, value)

  const input = document.createElement('input')
  input.type = 'range'
  input.min = String(options.min)
  input.max = String(options.max)
  input.step = String(options.step)
  input.value = String(options.get())
  input.setAttribute('aria-label', options.label)

  const render = (): void => {
    value.textContent = options.format(Number(input.value))
  }
  render()

  // Rendu à chaque mouvement, écriture à la fin du geste : suivre chaque
  // micro-déplacement déclencherait un recalcul de la carte par pixel parcouru.
  input.addEventListener('input', render)
  input.addEventListener('change', () => {
    options.set(Number(input.value))
    options.onChange?.()
  })

  wrapper.append(header, input)
  return wrapper
}

export interface ChoiceRowOptions {
  readonly label: string
  /** Valeur courante, affichée à droite en couleur primaire. */
  readonly value: string
  readonly onOpen: () => void
  readonly disabled?: boolean
  readonly summary?: string
}

/** Ligne cliquable ouvrant un dialogue de choix. */
export function choiceRow(options: ChoiceRowOptions): HTMLElement {
  const row = document.createElement('button')
  row.type = 'button'
  row.className = 'setting-row setting-row--clickable'
  row.disabled = options.disabled === true

  const label = document.createElement('span')
  label.className = 'setting-row__label'
  label.textContent = options.label

  const value = document.createElement('span')
  value.className = 'setting-row__value'
  value.textContent = options.value

  row.append(label, value)
  row.addEventListener('click', options.onOpen)

  if (options.disabled === true) {
    row.style.opacity = '0.5'
  }

  return options.summary === undefined ? row : withSummary(row, options.summary)
}

/** Empile un sous-titre explicatif sous une ligne de réglage. */
function withSummary(row: HTMLElement, text: string): HTMLElement {
  const wrapper = document.createElement('div')
  const summary = document.createElement('div')
  summary.className = 'setting-row__summary'
  summary.textContent = text
  wrapper.append(row, summary)
  return wrapper
}

/** Séparateur horizontal entre deux groupes de réglages. */
export function divider(): HTMLElement {
  const element = document.createElement('div')
  element.className = 'setting-divider'
  return element
}

/** Champ de saisie libre, avec libellé au-dessus. */
export function textField(options: {
  label: string
  value: string
  hint?: string
  inputMode?: string
}): { element: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('label')
  wrapper.className = 'field'

  const label = document.createElement('span')
  label.className = 'field__label'
  label.textContent = options.label

  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'field__control'
  input.value = options.value
  if (options.inputMode) input.inputMode = options.inputMode

  wrapper.append(label, input)

  if (options.hint !== undefined) {
    const hint = document.createElement('span')
    hint.className = 'field__hint'
    hint.textContent = options.hint
    wrapper.append(hint)
  }

  return { element: wrapper, input }
}
